import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { LeadsService } from "../../../services/user/leads.services"
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { useSelector } from "react-redux";
import { CountryService } from "../../../services/user/country.services";
import { StateService } from "../../../services/user/state.services";
import { ItemsService } from "../../../services/user/items.services";
import { LeadSourcesService } from "../../../services/user/leadsources.services";
import { StatusService } from "../../../services/user/statuses.services";
import { PriorityService } from "../../../services/user/priorities.services";
import { ProbabilityService } from "../../../services/user/probability.services";
import { Editor } from "@tinymce/tinymce-react";
import { UserServices } from "../../../services/user/user.services";
import striptags from "striptags";
import { CustomerService } from "../../../services/user/customer.services";
import Select from 'react-select';
import { getDateFormat, parseCultureDate } from "../../../utility";

const AddLeadComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const leadId = location.state?.leadId || 0;
  const isEditMode = !!leadId;
  const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [leadsources, setLeadSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [priority, setPriority] = useState([]);
  const [customer, setCustomer] = useState([]);
  const [probability, setProbability] = useState([]);
  const [users, setUsers] = useState([]);
  const [leadData, setLeadData] = useState(null);

  const formik = useFormik({
    initialValues: {
      isExistingCustomer: false,
      // leadDate: moment(new Date()).format('YYYY-MM-DD'),
      leadDate: new Date(),
      statusId: 0,
      leadSourcesId: 0,
      priorityId: 0,
      probabilityId: 0,
      userId: 0,
      companyName: "",
      position: "",
      contactName: "",
      email: "",
      website: "",
      phoneNumber: "",
      addressLine1: "",
      city: "",
      stateId: 0,
      pinCode: 0,
      countryId: 0,
      isActive: true,
      itemIds: [],
      leadDescription: "",
      leadValue: 0,
      customerId: 0,
      storeId: selectedStore
    },
    validationSchema: Yup.object({
      companyName: Yup.string().required("Company name is required"),
      phoneNumber: Yup.string().required("Phone number is required"),
      pinCode: Yup.number().required("pinCode is required"),
    }),
    onSubmit: async (values) => {
      try {
        //debugger
        let cleanedleadDescription = striptags(values.leadDescription);
        const data = {
          ...values,
          leadDate: values.leadDate ? new Date(values.leadDate).toISOString() : null,
          leadDescription: cleanedleadDescription,
          countryId: Number(values.countryId),
          stateId: Number(values.stateId),
          statusId: Number(values.statusId),
          userId: Number(values.userId),
          customerId: Number(values.customerId),
          leadSourcesId: Number(values.leadSourcesId),
          //priorityId: Number(values.priorityId),
          //probabilityId: Number(values.probabilityId),
          itemIds: Array.isArray(values.itemIds) ? (values.itemIds).map(id => Number(id)) : [Number(values.itemIds)],
          //itemIds: values.itemIds.map(id => Number(id)),
        };
        setLoading(true);
        if (isEditMode) {
          await LeadsService.updateLeadById(leadId, data);
          navigate("/leads");
        } else {
          await LeadsService.addNewLead(data);
          navigate("/leads");
        }
      } catch (error) {
        console.error("Error while submitting form:", error);
        handleNotify(
          "An error occurred while saving the lead.",
          TOASTER_TYPE.ERROR,
          TOASTER_POSITION.TOP_RIGHT
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    fetchCustomer()
    fetchCountry();
    fetchStates();
    fetchStatusData();
    fetchItems();
    fetchLeadSources();
    fetchPriority();
    fetchProbability();
    fetchUsers();
  }, [selectedStore]);

  useEffect(() => {
    if (isEditMode) {
      const fetchLeadDetail = async () => {
        try {
          const data = await LeadsService.getLeadById(leadId);
          //console.log("Parsed leadDate Date:", data.leadDate)
          setLeadData(data);
          if (data) {
            formik.setValues({
              isExistingCustomer: data?.isExistingCustomer,
              leadDate: parseCultureDate(data.leadDate),
              statusId: data?.statusId,
              leadSourcesId: data?.leadSourcesId,
              priorityId: data?.priorityId,
              probabilityId: data?.probabilityId,
              userId: data?.userId,
              companyName: data?.companyName,
              contactName: data?.contactName,
              position: data?.position,
              website: data?.website,
              phoneNumber: data?.phoneNumber,
              addressLine1: data?.address?.addressLine1,
              city: data?.address?.city,
              stateId: data?.address?.stateId,
              pinCode: data?.address?.pinCode,
              countryId: data?.address?.countryId,
              isActive: data?.isActive,
              itemIds: data?.items?.map(item => item?.itemId) || [],
              leadDescription: data?.leadDescription,
              leadValue: data?.leadValue,
              email: data?.email,
              // customerId: data?.customerId,
              customerId: data?.customer?.customerId,
              storeId: selectedStore
            });
          }

        } catch (error) {
          console.log("Error fetching lead details", error);
        }
      };
      fetchLeadDetail();
    }
  }, []);

  const fetchStates = async () => {
    try {
      const response = await StateService.getAllStates(selectedStore);
      if (response) {
        const sortedState = response.sort((a, b) => a.stateId - b.stateId);
        const stateOptions = sortedState.map(state => ({
          value: state.stateId,
          label: state.stateName,
        }));
        if (!formik.values.stateId && stateOptions.length > 0) {
          formik.setFieldValue("stateId", stateOptions[0].value);
        }
        setStates(stateOptions);
      }
      else {
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  };

  const fetchCountry = async () => {
    try {
      const response = await CountryService.getAllCountry(selectedStore);
      if (response) {
        const sortedCountry = response.sort((a, b) => a.countryId - b.countryId);
        const countryIdOptions = sortedCountry.map(country => ({
          value: country.countryId,
          label: country.countryName,
        }));
        if (!formik.values.countryId && countryIdOptions.length > 0) {
          formik.setFieldValue("countryId", countryIdOptions[0].value);
        }
        setCountries(countryIdOptions);
      }
      else {
        setCountries([]);
      }
    } catch (error) {
      console.error("Error fetching countries:", error);
    }
  };

  const fetchCustomer = async () => {
    try {
      const customerResponse = await CustomerService.getAllCustomers(selectedStore);
      if (customerResponse) {
        const sortedCustomer = customerResponse.sort((a, b) => a.customerId - b.customerId);
        const customerOptions = sortedCustomer.map(customer => ({
          value: customer.customerId,
          label: customer.companyName,
        }));

        setCustomer(customerOptions);

        if (formik.values.isExistingCustomer) {
          if (!formik.values.customerId && customerOptions.length > 0) {
            formik.setFieldValue("customerId", customerOptions[0].value);
          }
        } else {
          formik.setFieldValue("customerId", null);
        }
      } else {
        setCustomer([]);
      }
    } catch (error) {
      console.error("Error fetching Customer:", error);
    }
  };



  const fetchStatusData = async () => {
    const statusResponse = await StatusService.getStatusByType("Leads");
    const sortedStatus = statusResponse.sort((a, b) => a.statusId - b.statusId);
    const statusIdOptions = sortedStatus.map(status => ({
      value: status.statusId,
      label: status.statusName,
    }));

    setStatuses(statusIdOptions);
    if (!formik.values.statusId && statusIdOptions.length > 0) {
      formik.setFieldValue('statusId', statusIdOptions[0].value);
    }
  };

  const fetchLeadSources = async () => {
    try {
      const response = await LeadSourcesService.getAllLeadSources(selectedStore);
      const sortedLeadSource = response.sort((a, b) => a.leadSourcesId - b.leadSourcesId);
      const leadSourceOptions = sortedLeadSource.map(leadSource => ({
        value: leadSource.leadSourcesId,
        label: leadSource.leadSourceName,
      }));
      if (!formik.values.leadSourcesId && leadSourceOptions.length > 0) {
        formik.setFieldValue('leadSourcesId', leadSourceOptions[0].value)
      }
      setLeadSources(leadSourceOptions);
    } catch (error) {
      console.error("Error fetching leadSources:", error);
    }
  };

  const fetchPriority = async () => {
    try {
      const priorityResponse = await PriorityService.getPriorityByType("Leads");
      const sortedPriority = priorityResponse.sort((a, b) => a.priorityId - b.priorityId);
      const priorityOptions = sortedPriority.map(priority => ({
        value: priority.priorityId,
        label: priority.priorityName,
      }));
      if (!formik.values.priorityId && priorityOptions.length > 0) {
        formik.setFieldValue("priorityId", priorityOptions[0].value)
      }
      setPriority(priorityOptions);
    } catch (error) {
      console.error("Error fetching Priority:", error);
    }
  };

  const fetchProbability = async () => {
    try {
      const response = await ProbabilityService.getAllProbabilities(selectedStore);
      const sortedProbability = response.sort((a, b) => a.probabilityId - b.probabilityId);
      const probabilityOptions = sortedProbability.map(probability => ({
        value: probability.probabilityId,
        label: probability.probabilityName,
      }));
      if (!formik.values.probabilityId && probabilityOptions.length > 0) {
        formik.setFieldValue("probabilityId", probabilityOptions[0].value)
      }
      setProbability(probabilityOptions);
    } catch (error) {
      console.error("Error fetching probability:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await UserServices.getAllUsers(selectedStore);
      const sortedUsers = response.sort((a, b) => a.userId - b.userId);
      const usersOptions = sortedUsers.map(user => ({
        value: user.userId,
        label: `${user.username}(${user.role.roleName})`,
      }));
      if (!formik.values.userId && usersOptions.length > 0) {
        formik.setFieldValue("userId", usersOptions[0].value)
      }
      setUsers(usersOptions);
    } catch (error) {
      console.error("Error fetching Users:", error);
    }
  };

  const handleBackClick = () => {
    navigate("/leads");
  };

  const handleConvertToCustomerClick = () => {
    try {
      const leadData = formik.values;
      navigate('/customer/create', { state: { leadData } });
    } catch (error) {
      console.error('Error converting lead to customer:', error);
      handleNotify('Failed to create customer from lead.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
    }
  };


  const fetchItems = async () => {
    try {
      const response = await ItemsService.getAllItems();

      if (Array.isArray(response)) {
        const sorteItems = response.sort((a, b) => a.itemId - b.itemId);
        const itemOptions = sorteItems.map(item => ({
          value: item.itemId,
          label: item.itemName,
        }));
        if (!formik.values.itemIds && itemOptions.length > 0) {
          formik.setFieldValue('itemIds', itemOptions[0].value)
        }
        setItems(itemOptions);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching Items Data', error);
      setItems([]);
    }
  };

  return (
    <div className="page-wrapper m-0 p-5">
      <div className="page-content">
        <div className="page-breadcrumb d-flex align-items-center mb-3">
          <div
            className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
            onClick={handleBackClick}
          >
            Leads
          </div>
          <div className="ps-3">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0 p-0">
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? "Edit Lead" : "New Lead"}
                </li>
              </ol>
            </nav>
          </div>
          <div className="ms-auto">
            {isEditMode ? (
              <div className="btn-group" role="group" aria-label="Basic example">
                <button
                  type="button"
                  className="btn btn-outline-success py-2"
                  onClick={handleConvertToCustomerClick}
                >
                  <i className="bx bx-group" />
                  Convert to Customer
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary py-2"
                  onClick={handleBackClick}
                >
                  Back
                </button>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                onClick={handleBackClick}
              >
                Back
              </button>
            )}

          </div>
        </div>

        <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
          <div className="card-body form-container">
            <form onSubmit={formik.handleSubmit} className="mt-4">
              {/* Lead Date */}
              <div className="row mb-4">
                <div className="col-md-3 col-12">
                  <label htmlFor="leadDate" className="form-label d-flex">
                    Lead Date:
                  </label>
                  <div className="d-flex">
                    {/*<DatePicker
                      selected={moment(formik.values.leadDate, "DD-MM-YYYY").toDate()}
                      //onChange={handleDateChange}
                      onChange={(date) => {
                        formik.setFieldValue("leadDate", moment(date).format("DD-MM-YYYY"));
                      }}
                      onBlur={formik.handleBlur}
                      name="leadDate"
                      dateFormat="dd-MM-yyyy"
                      className={`form-control ${formik.touched.leadDate && formik.errors.leadDate ? 'is-invalid' : ''}`}
                      id="leadDate"
                    />*/}
                    <DatePicker
                      selected={formik.values.leadDate ? new Date(formik.values.leadDate) : null}
                      onChange={(date) => formik.setFieldValue("leadDate", date)}
                      onBlur={formik.handleBlur}
                      name="leadDate"
                      dateFormat={getDateFormat()}
                      className={`form-control ${formik.touched.leadDate && formik.errors.leadDate ? 'is-invalid' : ''}`}
                      id="leadDate"
                    />

                    {/* <DatePicker
                      selected={formik.values.leadDate}
                      // selected={parseCultureDate("17/08/2025")}
                      onChange={(date) => formik.setFieldValue("leadDate", date)}
                      onBlur={formik.handleBlur}
                      name="leadDate"
                      dateFormat={getDateFormat()}
                      // dateFormat="dd-MM-yyyy"
                      className={`form-control ${formik.touched.leadDate && formik.errors.activityDate ? 'is-invalid' : ''}`}
                      id="leadDate"
                    /> */}
                    {formik.touched.leadDate && formik.errors.leadDate ? (
                      <div className='text-danger'>{formik.errors.leadDate}</div>
                    ) : null}
                  </div>
                </div>
                {/* Is Existing Customer */}
                <div className="col-md-3 col-12 d-flex align-items-center justify-content-start mt-4 ps-5">
                  <label className="form-label">Is Existing Customer?:</label>
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      id="isExistingCustomerYes"
                      name="isExistingCustomer"
                      className="form-check-input"
                      value={true}
                      checked={formik.values.isExistingCustomer === true}
                      onChange={() => formik.setFieldValue("isExistingCustomer", true)}
                    />
                    <label className="form-check-label" htmlFor="isExistingCustomerYes">
                      Yes
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      id="isExistingCustomerNo"
                      name="isExistingCustomer"
                      className="form-check-input"
                      value={false}
                      checked={formik.values.isExistingCustomer === false}
                      onChange={() => formik.setFieldValue("isExistingCustomer", false)}
                    />
                    <label className="form-check-label" htmlFor="isExistingCustomerNo">
                      No
                    </label>
                  </div>
                </div>

                {/* Existing Customer Dropdown */}
                {formik.values.isExistingCustomer && (
                  <div className="col-md-6 col-12">
                    <label htmlFor="customerId" className="form-label d-flex">
                      Existing Customer:
                    </label>
                    <div className="text-start">
                      <Select
                        id="customerId"
                        name="customerId"
                        options={customer}
                        value={customer.find(option => option.value === formik.values.customerId)}
                        onChange={(selectedOption) => formik.setFieldValue('customerId', selectedOption.value)}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Status and Source */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="statusId" className="form-label d-flex">
                    Status:
                  </label>
                  <div className="text-start">
                    <Select
                      id="statusId"
                      name="statusId"
                      options={statuses}
                      value={statuses.find(option => option.value === formik.values.statusId)}
                      onChange={selectedOption => formik.setFieldValue('statusId', selectedOption.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="leadSourcesId" className="form-label d-flex">
                    Source:
                  </label>
                  <div className="text-start">
                    <Select
                      id="leadSourcesId"
                      name="leadSourcesId"
                      options={leadsources}
                      value={leadsources.find(option => option.value === formik.values.leadSourcesId)}
                      onChange={selectedOption => formik.setFieldValue('leadSourcesId', selectedOption.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Priority and Probability */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="priorityId" className="form-label d-flex">
                    Priority:
                  </label>
                  <div className="text-start">
                    <Select
                      id="priorityId"
                      name="priorityId"
                      options={priority}
                      value={priority.find(option => option.value === formik.values.priorityId)}
                      onChange={selectedOption => formik.setFieldValue('priorityId', selectedOption.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="probabilityId" className="form-label d-flex">
                    Probability:
                  </label>
                  <div className="text-start">
                    <Select
                      id="probabilityId"
                      name="probabilityId"
                      options={probability}
                      value={probability.find(option => option.value === formik.values.probabilityId)}
                      onChange={selectedOption => formik.setFieldValue('probabilityId', selectedOption.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Assigned To */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="userId" className="form-label d-flex">
                    Assigned To:
                  </label>
                  <div className="text-start">
                    <Select
                      id="userId"
                      name="userId"
                      options={users}
                      value={users.find(option => option.value === formik.values.userId)}
                      onChange={selectedOption => formik.setFieldValue('userId', selectedOption.value)}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="companyName" className="form-label d-flex">
                    Company Name <span className="text-danger">*</span>:
                  </label>
                  <input
                    type="text"
                    id="companyName"
                    name="companyName"
                    className={`form-control ${formik.touched.companyName && formik.errors.companyName ? "is-invalid" : ""}`}
                    placeholder="Enter Company Name"
                    value={formik.values.companyName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.companyName && formik.errors.companyName && (
                    <div className="invalid-feedback d-flex">{formik.errors.companyName}</div>
                  )}
                </div>
              </div>
              {/* Position and email */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="position" className="form-label d-flex">
                    Position:
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    placeholder="Enter Position Name"
                    className="form-control"
                    value={formik.values.position}
                    onChange={formik.handleChange}
                  />
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="email" className="form-label d-flex">
                    Email:
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="example@gmail.com"
                    className="form-control"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>

              {/* Website and Phone */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="website" className="form-label d-flex">
                    Website:
                  </label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    placeholder="Enter Website URL"
                    className="form-control"
                    value={formik.values.website}
                    onChange={formik.handleChange}
                  />
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="phoneNumber" className="form-label d-flex">
                    Phone <span className="text-danger">*</span>:
                  </label>
                  <input
                    type="text"
                    id="phoneNumber"
                    name="phoneNumber"
                    className={`form-control ${formik.touched.phoneNumber && formik.errors.phoneNumber ? "is-invalid" : ""}`}
                    placeholder="Enter Phone Number"
                    value={formik.values.phoneNumber}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                    <div className="invalid-feedback d-flex">{formik.errors.phoneNumber}</div>
                  )}
                </div>
              </div>

              {/* Country and State */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="countryId" className="form-label d-flex">
                    Country:
                  </label>
                  <div className="text-start">
                    <Select
                      id="countryId"
                      name="countryId"
                      options={countries}
                      value={countries.find(option => option.value === formik.values.countryId)}
                      onChange={(selectedOption) => formik.setFieldValue('countryId', selectedOption.value)}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="stateId" className="form-label d-flex">
                    State:
                  </label>
                  <div className="text-start">
                    <Select
                      id="stateId"
                      name="stateId"
                      options={states}
                      value={states.find(option => option.value === formik.values.stateId)}
                      onChange={(selectedOption) => formik.setFieldValue('stateId', selectedOption.value)}
                      menuPortalTarget={document.body}
                    />
                  </div>
                </div>
              </div>

              {/* City and Pincode */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="city" className="form-label d-flex">
                    City:
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    placeholder="Enter City Name"
                    className="form-control"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                  />
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="pinCode" className="form-label d-flex">
                    Pincode <span className="text-danger">*</span>:
                  </label>
                  <input
                    type="number"
                    id="pinCode"
                    name="pinCode"
                    className={`form-control ${formik.touched.pinCode && formik.errors.pinCode ? "is-invalid" : ""}`}
                    placeholder="Enter pinCode"
                    value={formik.values.pinCode}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.pinCode && formik.errors.pinCode && (
                    <div className="invalid-feedback d-flex">{formik.errors.pinCode}</div>
                  )}
                </div>
              </div>

              {/* Lead Value and Item */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="leadValue" className="form-label d-flex">
                    Lead Value:
                  </label>
                  <input
                    type="number"
                    id="leadValue"
                    name="leadValue"
                    placeholder="0.00"
                    className="form-control"
                    value={formik.values.leadValue}
                    onChange={formik.handleChange}
                  />
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="itemIds" className="form-label d-flex">
                    Item:
                  </label>
                  <div className="text-start">
                    <Select
                      id="itemIds"
                      name="itemIds"
                      options={items}
                      value={items.filter
                        (option => Array.isArray(formik.values.itemIds) && formik.values.itemIds.includes(option.value))}
                      onChange={(selectedOption) => formik.setFieldValue('itemIds', selectedOption ? selectedOption.map((option) => option.value) : [])}
                      menuPortalTarget={document.body}
                      isMulti
                    />
                  </div>
                </div>
              </div>
              {/* Address */}
              <div className="row mb-4">
                <div className="col-md-6 col-12">
                  <label htmlFor="contactName" className="form-label d-flex">
                    Contact Name:
                  </label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    className="form-control"
                    placeholder="Enter Contact Number"
                    value={formik.values.contactName}
                    onChange={formik.handleChange}
                  />
                </div>
                <div className="col-md-6 col-12">
                  <label htmlFor="addressLine1" className="form-label d-flex">
                    Address:
                  </label>
                  <textarea
                    id="addressLine1"
                    name="addressLine1"
                    rows={2}
                    placeholder="Enter Address"
                    className="form-control"
                    value={formik.values.addressLine1}
                    onChange={formik.handleChange}
                  />
                </div>

              </div>

              {/* Status Radio Buttons */}
              <div className="col-md-3 col-12 d-flex align-items-center justify-content-start mt-5">
                <div className="d-flex align-items-center mb-4">
                  <label className="form-label me-3">Status:</label>
                  <div className="form-check form-check-inline me-3">
                    <input
                      type="radio"
                      id="isActive"
                      name="isActive"
                      className="form-check-input"
                      value={true}
                      checked={formik.values.isActive === true}
                      onChange={() => formik.setFieldValue("isActive", true)}
                    />
                    <label className="form-check-label" htmlFor="active">
                      Active
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      type="radio"
                      id="inactive"
                      name="isActive"
                      className="form-check-input"
                      value={false}
                      checked={formik.values.isActive === false}
                      onChange={() => formik.setFieldValue("isActive", false)}
                    />
                    <label className="form-check-label" htmlFor="inactive">
                      Inactive
                    </label>
                  </div>
                </div>
              </div>

              <div className="row mt-2">
                <div className="col-12">
                  <label htmlFor="leadDescription" className="form-label d-flex">
                    Description:
                  </label>
                  <Editor
                    value={formik.values.leadDescription}
                    tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                    init={{
                      height: 300,
                      menubar: true,
                      plugins: [
                        "advlist autolink lists link image charmap print preview anchor",
                        "searchreplace visualblocks code fullscreen",
                        "insertdatetime media table paste code help wordcount",
                      ],
                      toolbar:
                        "undo redo | formatselect | bold italic backcolor | \
                        alignleft aligncenter alignright | bullist numlist outdent indent | \
                        link image",
                    }}
                    onEditorChange={(content) =>
                      formik.setFieldValue("leadDescription", content)
                    }
                  />
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                  onClick={handleBackClick}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                  disabled={loading}
                >
                  {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div >
    </div >
  );
};

export default AddLeadComponent;
